import { useState } from 'react';
import { BorrowerSelection, BorrowerType } from './constants/borrowerSelection';
import { BorrowerTypeField } from './components/BorrowerTypeField';
import { BorrowerEntity } from './components/BorrowerEntity/BorrowerEntity';
import { BorrowerWithBClient } from './components/BorrowerWithBClient/BorrowerWithBClient';

interface BorrowerSelectorProps {
    /** The selected entity. Changing it from outside (e.g. a loan loading in) resets the selector to it. */
    borrowerEntityId?: number;
    /** The selected B-Client agreement. Changing it from outside resets the selector to it. */
    brokerAgreementId?: number;
    /** Called with the whole selection on every change. */
    onChange: (selection: BorrowerSelection) => void;
    disabled?: boolean;
    required?: boolean;
    /** Shows the buttons to add a new entity and edit the selected one. */
    canEdit?: boolean;
}

/**
 * Picks who a loan is for: "Borrower Only" (a borrower entity) or "Borrower with B-Client" (a broker agreement). A new
 * version of BClientOrEntitySelector. It keeps the selection itself and hands the whole of it back through onChange.
 */
export const BorrowerSelector = ({
    borrowerEntityId,
    brokerAgreementId,
    onChange,
    disabled = false,
    required = true,
    canEdit = true,
}: BorrowerSelectorProps) => {
    // As BClientOrEntitySelector did: an agreement means B-Client, otherwise Borrower Only
    const getSelectionFromIds = (): BorrowerSelection => ({
        borrowerType: brokerAgreementId ? BorrowerType.BClient : BorrowerType.Entity,
        borrowerEntityId,
        brokerAgreementId,
    });

    const [selection, set_selection] = useState<BorrowerSelection>(getSelectionFromIds);
    const [lastIds, set_lastIds] = useState({ borrowerEntityId, brokerAgreementId });

    // Re-sync when the ids passed in change, unless they're just the ones this handed back through onChange
    if (lastIds.borrowerEntityId !== borrowerEntityId || lastIds.brokerAgreementId !== brokerAgreementId) {
        set_lastIds({ borrowerEntityId, brokerAgreementId });
        if (borrowerEntityId !== selection.borrowerEntityId || brokerAgreementId !== selection.brokerAgreementId) {
            set_selection(getSelectionFromIds());
        }
    }

    const updateSelection = (next: BorrowerSelection) => {
        set_selection(next);
        onChange(next);
    };

    // Clears the agreement either way, and the entity when switching to Borrower Only (it's kept for B-Client)
    const selectBorrowerType = (borrowerType: BorrowerType) =>
        updateSelection(
            borrowerType === BorrowerType.BClient
                ? { ...selection, borrowerType, brokerAgreementId: undefined }
                : { borrowerType, borrowerEntityId: undefined, brokerAgreementId: undefined }
        );

    const selectBorrowerEntity = (id: number | undefined) =>
        updateSelection({ borrowerType: BorrowerType.Entity, borrowerEntityId: id, brokerAgreementId: undefined });

    const selectBrokerAgreement = (id: number | undefined) =>
        updateSelection({ ...selection, borrowerType: BorrowerType.BClient, brokerAgreementId: id });

    // A disabled selector isn't validated (as in EditStdLoanModal)
    const isRequired = required && !disabled;

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <BorrowerTypeField
                value={selection.borrowerType}
                onChange={selectBorrowerType}
                disabled={disabled}
                required={required}
            />
            {selection.borrowerType === BorrowerType.BClient ? (
                <BorrowerWithBClient
                    brokerAgreementId={selection.brokerAgreementId}
                    onSelect={selectBrokerAgreement}
                    disabled={disabled}
                    required={isRequired}
                />
            ) : (
                <BorrowerEntity
                    borrowerEntityId={selection.borrowerEntityId}
                    onSelect={selectBorrowerEntity}
                    disabled={disabled}
                    required={isRequired}
                    canEdit={canEdit}
                />
            )}
        </div>
    );
};
